/**
 * HubTestReportTabs — 학습(/students-hub) 우측 "활동 피드" 컨테이너 (테스트 + 리포트 시계열 통합)
 *
 * 카드 chrome·헤더(학습기록 타이틀 + 좌:테스트 생성/우:리포트 생성)를 소유하고, 테스트/시험지 배정/
 * 리포트 데이터·모달 상태를 관리한다. 본문은 탭 없이 단일 피드
 * (HubActivityFeed)로, 테스트·시험지 배정 카드(좌측 말풍선)와 리포트 카드(우측 말풍선)를 시각
 * 내림차순으로 섞어 보여준다. 리포트 카드 클릭 시 HubReportModal(StudentsHub 로컬, DnaReportDraft)을 연다.
 *   - 리포트 목록/생성/삭제: v2 API (reportApi.fetchReportList/createStudentReport/deleteReport)
 *   - 시험지 배정 목록: v2 API (examPaperApi.fetchStudentAssignments) — v1 테스트 목록이 신규
 *     시험지를 담지 못해 따로 받는다(근거는 HubAssignmentCard 머리주석).
 *
 * 이원화: 공유 ReportModal에 의존하지 않는다(신 KY Report는 students-hub 전용).
 */

import React, { useState, useCallback, useEffect, useRef, lazy, Suspense } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FolderOpen, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import HubActivityFeed from './HubActivityFeed';
import { fetchReportList, deleteReport, createStudentReport } from './reportApi';
import { assignPaper, fetchAssignment, fetchStudentAssignments, describeApiError, AUTH_REDIRECT } from '../ExamPapersV2/examPaperApi';
import { fetchAllReportShares, buildActiveShareMap } from './shareApi';
import { assignmentIdForStudent, assignmentPending, assignmentToTest, testKey } from './assignmentResult';

const HubReportModal = lazy(() => import('./HubReportModal'));
const HubReportCreateModal = lazy(() => import('./HubReportCreateModal'));
const HubPaperPickerModal = lazy(() => import('./HubPaperPickerModal'));
// 시험지 제작 창은 시험지(/exam-papers)의 것을 **그대로** 연다. 2600줄짜리라 사본을 뜨는 순간
// 두 벌이 갈리고, 인쇄 v4 지면·개념 검색·조판이 통째로 두 번 유지된다.
const PaperComposeModal = lazy(() => import('../ExamPapersV2/PaperComposeModal'));
/**
 * 채점 창 — 배정 카드를 누르면 **이 화면 위에** 뜬다(오너 지시 2026-09-06). 페이지를 옮기지 않는다.
 * 채점기와 KaTeX 를 함께 끌고 오므로 다른 창들과 같이 lazy 로 둔다 — 학생을 훑기만 하는 동안에는
 * 내려받지 않는다.
 */
const AssignmentGradeModal = lazy(() => import('../ExamPapersV2/AssignmentGradeModal'));

function HubTestReportTabs({
  testHistory,
  selectedTest,
  isTestHistoryLoading,
  hasMoreTests,
  onTestSelect,
  /**
   * 고른 것을 **그대로 앉힌다**(토글하지 않는다). 배정은 고르는 데 왕복이 하나 붙어
   * (목록에 문항이 없다) 「먼저 앉히고 → 도착하면 덮기」로 두 번 부르는데, `onTestSelect` 는
   * 같은 것을 다시 주면 해제하는 토글이라 두 번째 호출이 방금 고른 것을 꺼 버린다.
   * 그래서 배정만 이 통로를 쓴다 — 무엇을 앉힐지는 여기서 판정한다.
   */
  onTestSetDirect = null,
  testHistoryRef,
  onLoadMore,
  selectedStudent,
  studentId,
  compact = false,
  onLearningChanged,
}) {
  const { getAccessToken } = useAuth();
  const sid = studentId ?? selectedStudent?.student_id ?? null;

  const [reportOpen, setReportOpen] = useState(false);
  const [reportEntry, setReportEntry] = useState(null); // 열람 중인 저장 리포트(meta.select 스코프용)
  const [createOpen, setCreateOpen] = useState(false);
  const [paperPickerOpen, setPaperPickerOpen] = useState(false);
  // 시험지 제작 창: null(닫힘) · 'new'(새 시험지) · id 없는 로컬 초안(기존 시험지 편집)
  const [composeFor, setComposeFor] = useState(null);
  // [시험지만 저장] 뒤 선택창으로 돌아갈 때 새 시험지를 즉시 골라 주는 일회성 복귀 사건.
  // 선택창 자체는 제작 중에도 마운트해 두므로 검색·필터·페이지·스크롤 상태가 그대로 남는다.
  const [paperPickerResume, setPaperPickerResume] = useState(null);
  const paperPickerResumeSeq = useRef(0);
  // 창이 닫힌 뒤에도 남는 유일한 설명 — 출제 성공·실패는 여기서만 말한다
  const [flash, setFlash] = useState(null); // {type:'info'|'error', text}
  const [assigning, setAssigning] = useState(false);
  const assigningRef = useRef(false); // 같은 tick의 더블클릭도 배정 1회로 가두는 동기 가드
  const assignRunSeq = useRef(0); // 학생 전환 뒤 도착한 이전 학생의 결과를 현재 화면에 앉히지 않는다
  const activeSidRef = useRef(sid);
  activeSidRef.current = sid;
  const [pendingAssignment, setPendingAssignment] = useState(null); // 저장 후 배정만 실패한 건
  const [recentAssignmentId, setRecentAssignmentId] = useState(null);

  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState(null);

  // 신규 시험지(v2) 배정 — 테스트 목록(v1)과 **다른 API** 라 상태를 따로 든다
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsError, setAssignmentsError] = useState(null);
  const assignSeq = useRef(0); // 학생을 빠르게 바꿀 때 옛 응답이 새 학생 자리에 앉지 않게

  /**
   * 지금 채점 중인 배정 — **주소가 진실이다**(`?grade=<배정 id>`, 오너 지시 2026-09-06
   * 「뒤로가기/앞으로가기 되도록」). 종전에는 이것이 로컬 state 라, 창이 떠 있는데 주소는
   * 학생 화면 그대로였다. 그 상태에서 뒤로가기를 누르면 창만 닫히는 것이 아니라 **학생 화면을
   * 통째로 벗어났고**, 창을 띄운 자리를 남에게 링크로 건넬 길도 없었다.
   *
   * 파라미터 이름 `grade` 는 **형제 화면에서 그대로 가져왔다** — 시험지 결과표가 같은 창을
   * `?tab=assign&grade=<aid>` 로 이미 쥐고 있다(ExamPapers.js, 오너 결정 2026-09-06 D1).
   * 같은 창을 여는 같은 성질의 값에 이름을 둘로 두면, 한쪽을 고칠 때 다른 쪽을 잊는다.
   *
   * 쿼리로 두는 이유(중첩 라우트가 아니라): 이 창은 학생 화면을 **덮는 것**이지 다른 화면이
   * 아니다. 배경의 목록·스크롤·탭이 그대로 살아 있어야 닫았을 때 하던 자리로 돌아온다.
   * 라우트 표(App.js)를 늘리지 않아도 되는 것은 덤이다 — 이 창은 3단 아래에 산다.
   */
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const gradeParam = searchParams.get('grade');
  // 주소는 남이 손으로 고칠 수 있다 — 정수가 아니면 열지 않는다(형제 화면과 같은 자).
  // 문자열 그대로 넘긴다: 채점기는 이 값을 URL 에 되싣기만 하고 셈하지 않는다.
  const gradingId = /^\d+$/.test(String(gradeParam ?? '')) ? gradeParam : null;

  /** 카드 → 창. 히스토리에 **쌓는다**(replace 가 아니다) — 그래야 뒤로가기가 창만 닫는다 */
  const openGrading = useCallback((aid) => {
    if (aid == null) return;
    const next = new URLSearchParams(location.search);
    next.set('grade', String(aid));
    navigate(
      { pathname: location.pathname, search: `?${next.toString()}` },
      /**
       * 「이 자리는 내가 밀어 넣었다」는 표식. 닫을 때 되감아도 되는지가 여기서 갈린다.
       *
       * 형제 화면은 같은 판정을 **ref** 로 든다(ExamPapers.js gradeOpenedByUsRef). 여기서는
       * 히스토리 엔트리에 실어 둔다 — ref 는 뒤로가기로 창이 닫힐 때 내려가므로, **앞으로가기로
       * 되살아난 창**을 닫으면 「내가 연 것이 아니다」로 읽혀 replace 로 빠지고 앞으로 갈 자리가
       * 사라진다. state 는 그 칸에 붙어 있어 뒤로·앞으로에 그대로 따라온다.
       */
      { state: { ...(location.state || {}), hubGradingPushed: true } },
    );
  }, [navigate, location.pathname, location.search, location.state]);

  /**
   * 배정 카드 본체 → **고르기**(창이 아니다 · 오너 지시 2026-09-07). 문제 미리보기를 이 시험지의
   * 문항으로 좁힌다. 창은 카드의 [>]가 연다.
   *
   * 왕복이 하나 붙는다 — 배정 **목록** 행에는 문항이 없고(계약 §1-1 은 수만 준다) 문항은
   * 상세(§2-9 items[])에만 있다. 그 왕복을 기다렸다 카드를 칠하면 누른 뒤 한참 아무 일도
   * 일어나지 않으므로, 먼저 앉히고(카드가 곧바로 골라지고 미리보기는 「불러오는 중」) 도착하면
   * 덮는다.
   */
  const selectSeq = useRef(0);
  const handleSelectAssignment = useCallback(async (row) => {
    const aid = row?.assignment_id;
    if (aid == null || !onTestSetDirect) return;
    // 골라진 것을 다시 누르면 해제한다 — 화면에서 끝나는 일이라 서버에 묻지 않는다
    if (testKey(selectedTest) === `a-${aid}`) { onTestSetDirect(null); return; }
    const seq = (selectSeq.current += 1);
    onTestSetDirect(assignmentPending(row));
    try {
      const d = await fetchAssignment(aid, getAccessToken);
      // 카드를 빠르게 갈아 눌렀다 — 늦게 온 문항을 지금 골라진 시험지 자리에 앉히지 않는다
      if (seq !== selectSeq.current) return;
      onTestSetDirect(assignmentToTest(d, row));
    } catch (e) {
      if (seq !== selectSeq.current) return;
      // 「불러오는 중」인 채로 굳히지 않는다 — 고르기를 되물리고 왜 안 됐는지 말한다
      onTestSetDirect(null);
      setFlash({ type: 'error', text: describeApiError(e, '시험지 문항을 불러오지 못했습니다.') });
    }
  }, [selectedTest, onTestSetDirect, getAccessToken]);

  /**
   * 채점한 결과를 **미리보기에 그 자리에서 갈아 끼운다.**
   *
   * 배정을 고르는 순간 받아 둔 정오답은 그때의 사본이다. 옆 창에서 O·X 를 매기면 그 사본이
   * 낡는데, 화면은 아무 말도 하지 않아 **틀린 색을 계속 보여 준다**(오너 지시 2026-09-07).
   * 목록만 다시 읽어서는 고쳐지지 않는다 — 문항별 정오답은 목록이 아니라 상세에만 있다.
   *
   * 「불러오는 중」으로 되돌리지 않는다. 이미 화면에 문항이 서 있고 바뀌는 것은 색뿐이라,
   * 여기서 pending 을 세우면 채점 한 번에 미리보기가 통째로 사라졌다 돌아온다.
   */
  const refreshSelectedAssignment = useCallback(async () => {
    const aid = selectedTest?.kind === 'assignment' ? selectedTest.assignment_id : null;
    if (aid == null || !onTestSetDirect) return;
    const seq = (selectSeq.current += 1);
    try {
      const d = await fetchAssignment(aid, getAccessToken);
      if (seq !== selectSeq.current) return;
      // 이름은 갖고 있던 것을 넘겨 둔다 — 상세에 제목이 없어도 칩의 이름이 번호로 주저앉지 않는다
      onTestSetDirect(assignmentToTest(d, { assignment_id: aid, paper_title: selectedTest.tree_name }));
    } catch (e) {
      if (seq !== selectSeq.current) return;
      /**
       * 지워졌거나 못 읽었다. 낡은 정오답을 계속 보여 주느니 고르기를 푼다.
       * 여기서는 말하지 않는다 — 이 함수를 부르는 것은 **방금 조작을 끝낸 채점 창**이고,
       * 실패했다면 그쪽이 이미 제 말로 알렸다. 같은 일을 두 번 알리면 어느 쪽이 진짜인지 흐려진다.
       */
      onTestSetDirect(null);
    }
  }, [selectedTest, onTestSetDirect, getAccessToken]);

  /**
   * 창 → 배경. 두 갈래다.
   *  · 내가 밀어 넣은 자리면 **되감는다** — 앞으로가기로 같은 창을 다시 열 수 있다.
   *  · 주소를 직접 들고 들어왔으면 되감을 곳이 없다(앱 밖으로 나간다). 파라미터만 지운다.
   */
  const closeGrading = useCallback(() => {
    if (location.state?.hubGradingPushed) {
      navigate(-1);
      return;
    }
    const next = new URLSearchParams(location.search);
    next.delete('grade');
    const search = next.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [navigate, location.pathname, location.search, location.state]);
  const [creating, setCreating] = useState(false);
  const [deletingIds, setDeletingIds] = useState(() => new Set()); // 삭제 진행 중인 report_id (버튼 비활성·중복 DELETE 차단)
  const deletingRef = useRef(deletingIds); // 동기(리렌더 이전) 중복 클릭까지 막는 in-flight 가드

  // report_id → 유효 공유(임시 토큰) 1건. 카드 배지·모달 발행 UI 공용.
  const [shareMap, setShareMap] = useState(() => new Map());
  const shareReqSeq = useRef(0); // 발행→취소 연속 조작 시 순서 역전된 응답이 최신 맵을 덮지 않게

  const loadShares = useCallback(async () => {
    if (!sid) return;
    const seq = ++shareReqSeq.current;
    try {
      const { items } = await fetchAllReportShares(sid, getAccessToken);
      if (seq === shareReqSeq.current) setShareMap(buildActiveShareMap(items));
    } catch (_) {
      // 공유 상태는 부가 정보 — 실패 시 이전 맵 유지. 빈 맵 확정은 유효 공유를 '없음'으로
      // 오표시해 중복 발행을 유도한다(발행해도 기존 링크는 살아 있음).
    }
  }, [sid, getAccessToken]);

  const loadReports = useCallback(async () => {
    if (!sid) return;
    setReportsLoading(true);
    setReportsError(null);
    try {
      const list = await fetchReportList(sid, getAccessToken);
      setReports(list);
    } catch (e) {
      setReportsError(e?.message || '리포트 목록을 불러오지 못했습니다.');
      setReports([]);
    } finally {
      setReportsLoading(false);
    }
  }, [sid, getAccessToken]);

  /**
   * 그 학생의 v2 배정 전부. `is_closed` 를 안 보낸다 — 진행 중과 마감분이 **한 피드에 함께**
   * 서야 학습기록이 시계열로 읽힌다(마감분만 보면 방금 출제한 것이 안 보인다).
   * 계약에 페이지네이션이 없어 전량 전제다(examPaperApi.fetchStudentAssignments 주석과 같은 판단).
   */
  const loadAssignments = useCallback(async () => {
    if (!sid) return;
    const seq = (assignSeq.current += 1);
    setAssignmentsLoading(true);
    setAssignmentsError(null);
    try {
      const items = await fetchStudentAssignments(sid, {}, getAccessToken);
      if (seq !== assignSeq.current) return;
      setAssignments(Array.isArray(items) ? items : []);
    } catch (e) {
      if (seq !== assignSeq.current) return;
      setAssignmentsError(e?.message || '시험지 목록을 불러오지 못했습니다.');
      setAssignments([]);
    } finally {
      if (seq === assignSeq.current) setAssignmentsLoading(false);
    }
  }, [sid, getAccessToken]);

  /**
   * 채점 창에서 무언가 바뀌었다 — 저장·마감·재오픈·삭제 네 갈래가 모두 이리로 온다.
   * 목록·미리보기의 정오답과 학생 목록의 레이팅을 갱신한다.
   * 창이 떠 있는 채로도 배경이 따라 바뀐다 — 저장할 때마다 불리기 때문이다.
   */
  const handleGradingChanged = useCallback(() => {
    loadAssignments();
    refreshSelectedAssignment();
    onLearningChanged?.();
  }, [loadAssignments, refreshSelectedAssignment, onLearningChanged]);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  useEffect(() => {
    assignRunSeq.current += 1;
    assigningRef.current = false;
    setAssigning(false);
    setRecentAssignmentId(null);
    setPendingAssignment(null);
    setFlash(null);
    setReportOpen(false);
    setReportEntry(null);
    setCreateOpen(false);
    setPaperPickerOpen(false);
    setComposeFor(null);
    setPaperPickerResume(null);
  }, [sid]);

  // 학생 변경 시(또는 마운트 시) 리포트 목록 로드 — 활동 피드가 테스트와 시계열 병합
  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // 성공 알림만 스스로 사라진다. 실패는 남긴다 — 사유를 읽기 전에 지워지면 왜 막혔는지 알 길이 없다.
  useEffect(() => {
    if (!flash || flash.type === 'error' || assigning) return undefined;
    const t = setTimeout(() => setFlash(null), 5000);
    return () => clearTimeout(t);
  }, [flash, assigning]);

  /**
   * 이미 있는 시험지를 이 학생에게 배포한다. 제작 창의 [저장하고 …출제]와 시험지 고르기 창의
   * 출제가 **같은 자리로 모인다** — 성공하면 피드를 다시 읽어 방금 나간 것이 카드로 선다.
   *
   * 시험 정보(예약 오픈·자동 마감 등)는 싣지 않는다(오너 결정 2026-09-03 — 검토 중).
   * 자리는 HubPaperPickerModal.handleAssign 주석에 적어 두었다.
   */
  const runAssign = useCallback((paperId, label, { savedNow = false } = {}) => {
    // null 은 요청을 시작하지 않았다는 동기 신호다. 제작창은 이 값을 보고 실제로 접수된 경우에만
    // 닫힌다 — 느린 선행 출제와 겹쳤다고 저장된 시험지를 말없이 버리지 않는다.
    if (
      !sid
      || paperId == null
      || assigningRef.current
      || String(activeSidRef.current) !== String(sid)
    ) return null;
    const requestSid = sid;
    const runSeq = (assignRunSeq.current += 1);
    const isCurrent = () => runSeq === assignRunSeq.current
      && String(activeSidRef.current) === String(requestSid);
    assigningRef.current = true;
    setAssigning(true);
    setFlash({ type: 'info', text: '출제하는 중…' });
    return (async () => {
      try {
        const d = await assignPaper(paperId, { studentIds: [Number(sid)] }, getAccessToken);
        if (d === AUTH_REDIRECT) return false; // 401 → /signin 이동 중. 출제했다고 말하지 않는다
        if (!isCurrent()) return false;
        const assignmentId = assignmentIdForStudent(d, sid);
        setRecentAssignmentId(assignmentId);
        setPendingAssignment(null);
        setFlash({ type: 'info', text: `출제했습니다 — ${label || `시험지 #${paperId}`}` });
        await loadAssignments();
        return true;
      } catch (e) {
        if (!isCurrent()) return false;
        setPendingAssignment({ paperId, label, savedNow });
        setFlash({
          type: 'error',
          text: savedNow
            ? `시험지는 저장됐지만 출제하지 못했습니다. ${describeApiError(e, '')}`.trim()
            : describeApiError(e, '출제하지 못했습니다.'),
        });
        return false;
      } finally {
        if (isCurrent()) {
          assigningRef.current = false;
          setAssigning(false);
        }
      }
    })();
  }, [sid, getAccessToken, loadAssignments]);

  const handleOpenReport = useCallback((report) => {
    setReportEntry(report || null);
    setReportOpen(true);
  }, []);

  const handleCreateReport = useCallback(async (conceptIds) => {
    if (!sid || creating) return;
    setCreating(true);
    try {
      await createStudentReport(sid, conceptIds, getAccessToken);
      setCreateOpen(false);
      await loadReports();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.message || '리포트 생성에 실패했습니다.');
    } finally {
      setCreating(false);
    }
  }, [sid, creating, getAccessToken, loadReports]);

  const handleDeleteReport = useCallback(async (report) => {
    const id = report?.report_id;
    if (id == null || deletingRef.current.has(id)) return; // 진행 중이면 중복 DELETE 차단(동기 가드)
    // eslint-disable-next-line no-alert
    if (!window.confirm('이 리포트를 삭제할까요?')) return;
    deletingRef.current = new Set(deletingRef.current).add(id);
    setDeletingIds(deletingRef.current);
    try {
      await deleteReport(id, getAccessToken);
      setReports((prev) => prev.filter((r) => r.report_id !== id));
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.message || '리포트 삭제에 실패했습니다.');
    } finally {
      const next = new Set(deletingRef.current);
      next.delete(id);
      deletingRef.current = next;
      setDeletingIds(next);
    }
  }, [getAccessToken]);

  return (
    <div className="relative flex min-h-0 min-w-[300px] flex-1 flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
      {/* 헤더 — 학습기록 타이틀(가운데) + 좌:시험지 선택 기반 테스트 생성 / 우:리포트 생성 */}
      <div className={`flex shrink-0 items-center gap-2 border-b ${compact ? 'px-2.5 py-2' : 'px-3 py-2.5'}`}>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => {
              setPaperPickerResume(null);
              setPaperPickerOpen(true);
            }}
            disabled={!sid || assigning}
            aria-label="테스트 생성"
            title="시험지를 선택하거나 새로 만들어 이 학생에게 출제합니다"
            className={`inline-flex items-center gap-1 rounded-full border border-primary/40 font-semibold text-primary transition-colors hover:bg-primary/5 disabled:opacity-40 ${compact ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'}`}
          >
            <FolderOpen size={compact ? 13 : 14} strokeWidth={2.5} />
            테스트 생성
          </button>
        </div>

        <h3 className={`min-w-0 flex-1 truncate text-center font-semibold text-gray-700 ${compact ? 'text-xs' : 'text-sm'}`}>학습기록</h3>

        <div className="shrink-0">
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            disabled={!sid || creating}
            className={`inline-flex items-center gap-1 rounded-full bg-primary font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40 ${compact ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'}`}
            title={creating ? '리포트 생성 중...' : '리포트 생성'}
          >
            {creating ? (
              <span className={`animate-spin rounded-full border-2 border-white border-t-transparent ${compact ? 'size-3' : 'size-3.5'}`} />
            ) : (
              <Plus size={compact ? 13 : 14} strokeWidth={2.5} />
            )}
            리포트 생성
          </button>
        </div>
      </div>

      {/* 출제 결과 — 창이 닫힌 뒤에도 남는 유일한 설명이다. 성공은 5초 뒤 사라지고 실패는 남는다 */}
      {flash && (
        <div
          role={flash.type === 'error' ? 'alert' : 'status'}
          aria-live={flash.type === 'error' ? 'assertive' : 'polite'}
          className={`flex shrink-0 items-center justify-between gap-2 border-b px-3 py-1.5 text-[11px] ${
            flash.type === 'error' ? 'bg-danger/5 text-danger' : 'bg-primary/5 text-primary'
          }`}
        >
          <span className="min-w-0 flex-1">{flash.text}</span>
          {pendingAssignment && (
            <button
              type="button"
              onClick={() => runAssign(
                pendingAssignment.paperId,
                pendingAssignment.label,
                { savedNow: pendingAssignment.savedNow },
              )}
              disabled={assigning}
              className="shrink-0 rounded-full border border-current px-2 py-0.5 font-semibold disabled:opacity-50"
            >
              다시 출제
            </button>
          )}
          <button
            type="button"
            onClick={() => { setFlash(null); setPendingAssignment(null); }}
            aria-label="알림 닫기"
            className="shrink-0 rounded-full px-1 opacity-60 transition-opacity hover:opacity-100"
          >
            ×
          </button>
        </div>
      )}

      {/* 단일 활동 피드: 테스트·시험지 배정(좌) + 리포트(우) 시계열 병합 */}
      <HubActivityFeed
        testHistory={testHistory}
        reports={reports}
        assignments={assignments}
        selectedTest={selectedTest}
        isTestHistoryLoading={isTestHistoryLoading}
        reportsLoading={reportsLoading}
        reportsError={reportsError}
        assignmentsLoading={assignmentsLoading}
        assignmentsError={assignmentsError}
        onReloadAssignments={loadAssignments}
        hasMoreTests={hasMoreTests}
        onTestSelect={onTestSelect}
        onOpenReport={handleOpenReport}
        onDeleteReport={handleDeleteReport}
        onReloadReports={loadReports}
        shareMap={shareMap}
        deletingReportIds={deletingIds}
        testHistoryRef={testHistoryRef}
        onLoadMore={onLoadMore}
        compact={compact}
        recentAssignmentId={recentAssignmentId}
        returnTo={sid == null ? '/students-hub' : `/students-hub/student/${sid}`}
        onOpenAssignment={openGrading}
        onSelectAssignment={onTestSetDirect ? handleSelectAssignment : null}
      />

      {/* 채점 창. 닫으면 이 화면 그대로다 — 목록이 배경에 남아 다음 학생 카드를 바로 누를 수 있다.
          채점이 서버에 남으면 카드의 「채점 n/총」이 낡으므로 배정 목록만 다시 읽는다.
          ⚠ **뒤로가기로 닫힐 때는 미저장 확인을 지나지 않는다.** 주소가 먼저 바뀌고 창이
          언마운트되므로 물을 자리가 없다(AssignmentGradeModal 머리주석의 같은 사정).
          막지 못하는 대신 미저장 건수는 창 안에 상시로 떠 있다. */}
      {gradingId != null && (
        <Suspense fallback={null}>
          <AssignmentGradeModal
            /* 배정을 갈아 열 때 새로 세운다 — 형제 화면과 같은 규칙(ExamPapers.js `g-${id}`).
               같은 창을 재활용하면 앞 배정의 채점 초안·토스트가 다음 학생 자리에 남는다. */
            key={`g-${gradingId}`}
            assignmentId={gradingId}
            isOpen
            onClose={closeGrading}
            onChanged={handleGradingChanged}
          />
        </Suspense>
      )}

      {reportOpen && (
        <Suspense fallback={null}>
          <HubReportModal
            isOpen={reportOpen}
            onClose={() => setReportOpen(false)}
            reportId={(reportEntry && reportEntry.report_id) || null}
            studentId={sid}
            selectedConceptIds={(reportEntry && reportEntry.meta && reportEntry.meta.select && reportEntry.meta.select.concept_ids) || []}
            savedFormulaVersion={(reportEntry && reportEntry.meta && reportEntry.meta.formula_version) || null}
            share={reportEntry ? shareMap.get(reportEntry.report_id) || null : null}
            onSharesChanged={loadShares}
          />
        </Suspense>
      )}

      {/* 테스트 생성 — 시험지를 그대로 출제하거나, id 없는 로컬 초안으로 편집 창에 넘긴다 */}
      {paperPickerOpen && (
        <Suspense fallback={null}>
          <HubPaperPickerModal
            isOpen={paperPickerOpen}
            suspended={composeFor != null}
            resumeEvent={paperPickerResume}
            onClose={() => {
              setPaperPickerOpen(false);
              setPaperPickerResume(null);
            }}
            student={selectedStudent || { student_id: sid }}
            /* 배포는 그 창이 이미 끝냈다 — 여기서 또 부르지 않는다(두 번 나가면 배정이 두 개 생긴다) */
            onAssigned={({ title, count, assignmentId } = {}) => {
              setPaperPickerOpen(false);
              setPaperPickerResume(null);
              setRecentAssignmentId(assignmentId ?? null);
              setFlash({ type: 'info', text: `출제했습니다 — ${title || '시험지'}${count ? ` ${count}문항` : ''}` });
              loadAssignments();
            }}
            onCompose={(paper) => {
              setPaperPickerResume(null);
              setComposeFor(paper == null ? 'new' : paper);
            }}
          />
        </Suspense>
      )}

      {/* 시험지 제작 창(시험지 메뉴와 같은 부품) — 새로 만들거나, 로컬 초안을 고쳐서 출제한다.
          ⚠ 여기에 **원본 시험지를 그대로 물리지 않는다.** 이 창의 저장은 기존 시험지에 대해
          PATCH 라, 원본을 열면 이미 다른 학생에게 나간 그 시험지가 바뀐다. 선택창은 원본 id를
          제거한 초안만 넘기며, 사용자가 저장을 확정할 때 처음 POST한다(오너 결정 2026-09-03). */}
      {composeFor && (
        <Suspense fallback={null}>
          <PaperComposeModal
            isOpen
            paper={composeFor === 'new' ? null : composeFor}
            assignLabel={`저장하고 ${selectedStudent?.student_name || '이 학생'}에게 출제`}
            assignmentTargetName={selectedStudent?.student_name || '이 학생'}
            assignPrimary
            cancelLabel="시험지 선택으로"
            closeConfirmMessage="시험지 선택으로 돌아가면 지금까지 편집한 내용이 사라집니다."
            closeConfirmText="변경 버리고 돌아가기"
            onClose={() => setComposeFor(null)}
            /* [저장]만 눌렀을 때 — 시험지는 생겼지만 **아직 아무에게도 안 갔다.** 그 말을 하지 않으면
               사용자는 출제된 줄 안다. [저장하고 …출제]는 onSaved 다음에 onAssignRequested 가
               이어지고, 그쪽의 runAssign 이 이 알림을 지우고 결과로 덮는다. */
            onSaved={(paperId, { thenAssign = false } = {}) => {
              if (thenAssign) return;
              setComposeFor(null);
              setPaperPickerResume({
                key: (paperPickerResumeSeq.current += 1),
                paperId,
                message: '시험지를 저장했습니다 — 아직 출제되지 않았습니다. 내용을 확인한 뒤 바로 출제할 수 있습니다.',
              });
            }}
            onAssignRequested={(paperId, meta = {}) => {
              const task = runAssign(
                paperId,
                meta.title || `시험지 #${paperId}`,
                { savedNow: meta.savedNow === true },
              );
              if (task != null) {
                // 출제 요청을 접수한 뒤에는 기존 흐름대로 Hub의 진행/성공/실패 알림이 맡는다.
                // 선택창까지 닫아야 실패 시 그 뒤에 가려진 [다시 출제]를 바로 쓸 수 있다.
                setComposeFor(null);
                setPaperPickerOpen(false);
                setPaperPickerResume(null);
              }
            }}
          />
        </Suspense>
      )}

      {createOpen && (
        <Suspense fallback={null}>
          <HubReportCreateModal
            isOpen={createOpen}
            onClose={() => setCreateOpen(false)}
            studentId={sid}
            onCreate={handleCreateReport}
            creating={creating}
          />
        </Suspense>
      )}
    </div>
  );
}

export default HubTestReportTabs;
